import { Toggle } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ValueStepper from './ValueStepper';

import type { KeyResult } from '@/types/objectives';

import { NumberInput } from '@/components/NumberInput/NumberInput';
import { VALUE_PILL, VALUE_PILL_INPUT } from '@/components/objectives/objectiveStyles';
import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { DEFAULT_CURRENCY_SYMBOL } from '@/utils/objectivesFormat';

interface KeyResultProgressControlProps {
    keyResult: KeyResult;
    /** Persists a new value through the measurement's single `useKeyResultProgressCommit`: the bar
     * beside this control writes through the same one, so the two can never send competing writes
     * for the same measurement. */
    onCommit: (nextValue: number) => Promise<unknown>;
    /** True while any write of this measurement is in flight, whichever control started it. */
    isPending: boolean;
}

export default function KeyResultProgressControl({
    keyResult,
    onCommit,
    isPending,
}: KeyResultProgressControlProps) {
    const { t } = useTranslation();
    // The number being typed, kept until its save lands: a refetch meanwhile leaves it alone.
    const [typedValue, setTypedValue] = useState<number | null>(null);

    const commit = async (nextValue: number) => {
        await onCommit(nextValue);
        setTypedValue(null);
    };

    const commitSettledValue = (settledValue: number) => {
        if (settledValue === keyResult.currentValue) {
            setTypedValue(null);
            return;
        }

        void commit(settledValue);
    };

    if (keyResult.unit === KeyResultUnit.YES_NO) {
        const isYes = keyResult.currentValue > 0;

        return (
            <div
                className={`${
                    isYes ? 'border-primary bg-surface-field' : 'border-obj-line bg-white'
                } flex items-center gap-3 rounded-full border px-4 py-2`}
                data-testid={`key-result-progress-${keyResult.id}-wrapper`}
            >
                <Toggle
                    size={'md'}
                    checked={isYes}
                    onChange={(checked) => commit(checked ? 1 : 0)}
                    disabled={isPending}
                    // The label doubles as the value here (prototype "YES"/"NO"), not a static
                    // "current value" caption.
                    leftLabel={t(isYes ? 'objectives.form.yes' : 'objectives.form.no')}
                    ariaLabel={keyResult.title}
                    dataTestId={`key-result-progress-${keyResult.id}`}
                />
            </div>
        );
    }

    const prefix =
        keyResult.unit === KeyResultUnit.MONETARY
            ? keyResult.currencySymbol || DEFAULT_CURRENCY_SYMBOL
            : undefined;
    const suffix = keyResult.unit === KeyResultUnit.PERCENTAGE ? '%' : undefined;

    return (
        <div
            className={`${VALUE_PILL} gap-3 px-4 py-1`}
            data-testid={`key-result-progress-${keyResult.id}-wrapper`}
        >
            {prefix && <span className={'text-obj-muted text-[11px] font-medium'}>{prefix}</span>}

            <NumberInput
                value={typedValue ?? keyResult.currentValue}
                onChange={setTypedValue}
                onBlur={commitSettledValue}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.currentTarget.blur();
                    }
                }}
                disabled={isPending}
                ariaLabel={keyResult.title}
                className={`${VALUE_PILL_INPUT} w-[56px] text-[11px]`}
                dataTestId={`key-result-progress-${keyResult.id}`}
            />

            {suffix && <span className={'text-obj-muted text-[11px] font-medium'}>{suffix}</span>}

            <ValueStepper
                onIncrease={() => commit(keyResult.currentValue + 1)}
                onDecrease={() => commit(keyResult.currentValue - 1)}
                disabled={isPending}
                dataTestIdPrefix={`key-result-progress-${keyResult.id}`}
            />
        </div>
    );
}
